// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormField, Input, Select } from '@amzn/awsui-components-react-v3/polaris';
import { useRowEditor } from '../../hooks/useRowEditor';
import { RowEditor } from '../../hooks/useRowEditor';
import { ARC_FILTERING_PLACEHOLDER, arcKey, describeArc, LOCALE_FIELDS } from '../../utils/arcRows';
import {
  ArcColumn,
  ArcEditRow,
  ArcField,
  ArcRow,
  ArcRows,
  LanguagePair,
} from '../../utils/arcRows';
import { Locale } from '../../utils/locales';
import { ApiError, SaveFailure } from '../../utils/serverErrors';
import ListTable, { useScrollIntoView } from './ListTable';
import { ListColumn } from './ListTable';
import { localeColumn, removeRowColumn, rowNumberColumn } from './RowEditorControls';
import { RowItem } from './RowEditorControls';
import SpreadsheetUpload from './SpreadsheetUpload';

/**
 * The edit table and the spreadsheet upload of a per-arc list built with utils/arcRows. Used by the
 * base rates page and the TER group create and detail pages.
 */

const NO_LOCALES: Locale[] = [];

// Shows a locale outside edit mode.
type LocaleDisplay = (code: string | null) => string;

export type ArcRowsEditor<T, V extends string> = RowEditor<ArcEditRow<T, V>, ArcField<V>>;

/**
 * Edits the loaded `items`, which must be stable between renders; a new list, such as a reloaded
 * or saved one, resets the editor.
 */
export function useArcRowsEditor<T, V extends string>(
  list: ArcRows<T, V>,
  items: T[],
  locales: Locale[] | null,
  {
    emptyMessage,
    onSave,
  }: {
    // Shown when saving with no rows.
    emptyMessage: string;
    // Rejects with the API error when the save fails.
    onSave: (items: T[]) => Promise<unknown>;
  }
): ArcRowsEditor<T, V> {
  const loadedRows = useMemo(() => items.map(item => list.toEditRow(item)), [list, items]);
  return useRowEditor<ArcEditRow<T, V>, ArcField<V>>({
    loadedRows,
    newRow: list.toEditRow,
    validate: rows => list.validateRows(rows, locales),
    keyFields: LOCALE_FIELDS,
    keyOf: arcKey,
    emptyMessage,
    onSave: rows => onSave(rows.map(list.toItem)),
    describeSaveError: list.describeSaveError,
  });
}

function valueColumn<T, V extends string>(
  editor: ArcRowsEditor<T, V>,
  { key, header, numeric, step, options, placeholder, normalize }: ArcColumn<V>
): ListColumn<RowItem<ArcEditRow<T, V>>> {
  const selectOptions = options && options.map(option => ({ label: option, value: option }));
  return {
    id: key,
    header,
    cell: ({ row }): JSX.Element => <span>{row[key]}</span>,
    editCell: ({ row, index }): JSX.Element => (
      <FormField id={`${key}-${index}-field`} errorText={editor.errorFor(index, key)}>
        {selectOptions ? (
          <Select
            id={`${key}-${index}`}
            // A loaded value missing from the options still shows, so its error can be read.
            selectedOption={row[key] ? { label: row[key], value: row[key] } : null}
            options={selectOptions}
            placeholder={placeholder}
            // Tables scroll their cells, which would clip a dropdown rendered inside them.
            expandToViewport
            invalid={Boolean(editor.errorFor(index, key))}
            disabled={editor.submitting}
            onChange={({ detail }): void =>
              editor.changeRow(index, key, detail.selectedOption.value || '')
            }
          />
        ) : (
          <Input
            id={`${key}-${index}`}
            type={numeric ? 'number' : 'text'}
            inputMode={numeric ? 'decimal' : 'text'}
            step={step}
            placeholder={placeholder}
            value={row[key]}
            disabled={editor.submitting}
            onChange={({ detail }): void =>
              editor.changeRow(index, key, normalize ? normalize(detail.value) : detail.value)
            }
          />
        )}
      </FormField>
    ),
  };
}

interface ArcRowsTableProps<T, V extends string> {
  id: string;
  list: ArcRows<T, V>;
  editor: ArcRowsEditor<T, V>;
  // Null until the locales have loaded; editing needs them.
  locales: Locale[] | null;
  display: LocaleDisplay;
  // Element ids of each row's own error and remove button; the row index is appended.
  rowErrorId: string;
  removeId: string;
  header: React.ReactNode;
  empty: string;
}

/** The rows, with changed cells and every cell of an added row highlighted while editing. */
export function ArcRowsTable<T extends { languagePair: LanguagePair }, V extends string>({
  id,
  list,
  editor,
  locales,
  display,
  rowErrorId,
  removeId,
  header,
  empty,
}: ArcRowsTableProps<T, V>): JSX.Element {
  type Item = RowItem<ArcEditRow<T, V>>;
  const rowId = `${id}Row`;
  const scrollIntoView = useScrollIntoView();
  const { revealedRow } = editor;
  useEffect(() => {
    if (revealedRow) {
      scrollIntoView(`${rowId}-${revealedRow.index}`);
    }
  }, [revealedRow, rowId, scrollIntoView]);

  const columns: ListColumn<Item>[] = [
    rowNumberColumn(editor, rowErrorId, rowId),
    ...LOCALE_FIELDS.map(field =>
      localeColumn(editor, field, list.fieldLabels[field], locales || NO_LOCALES, display)
    ),
    ...list.columns.map(column => valueColumn(editor, column)),
    removeRowColumn(editor, removeId),
  ];

  return (
    <ListTable
      id={id}
      items={editor.rows.map((row, index) => ({ row, index }))}
      columns={columns}
      filterText={({ row }: Item): string => describeArc(row)}
      filteringPlaceholder={ARC_FILTERING_PLACEHOLDER}
      filtering={{ text: editor.filteringText, onChange: editor.setFilteringText }}
      // A row added while editing has no locales yet, so no filter would match it.
      alwaysShow={({ row }: Item): boolean => !row.original}
      editing={editor.editing}
      isEdited={({ row }: Item, columnId: string): boolean =>
        !row.original || list.changedFields(row).includes(columnId as ArcField<V>)
      }
      trackBy={(item: Item): string => String(item.row.key)}
      header={header}
      empty={empty}
    />
  );
}

interface ArcRowsUploadProps<T, V extends string> {
  list: ArcRows<T, V>;
  // Null until the locales have loaded; creating needs them.
  locales: Locale[] | null;
  display: LocaleDisplay;
  // Element ids start with this.
  idPrefix: string;
  title: string;
  description: string;
  templateUrl: string;
  emptyMessage: string;
  // False while anything else the create needs is missing.
  ready: boolean;
  // Rejects with the API error when the create fails.
  onCreate: (items: T[]) => Promise<unknown>;
  // The list's own by default.
  describeSaveError?: (error: ApiError, rowCount: number) => SaveFailure<ArcField<V>>;
  conflictHeader: string;
  onReload: () => void;
  // Orders the preview, which otherwise follows the spreadsheet.
  compareRows?: (a: LanguagePair, b: LanguagePair) => number;
  // Other fields the create needs, shown above the template link.
  children?: React.ReactNode;
}

/** Creates the list from an uploaded spreadsheet with one row per arc. */
export function ArcRowsUpload<T, V extends string>({
  list,
  locales,
  display,
  ready,
  onCreate,
  describeSaveError,
  compareRows,
  ...uploadProps
}: ArcRowsUploadProps<T, V>): JSX.Element {
  const validate = useCallback((rows: ArcRow<V>[]) => list.validateRows(rows, locales), [
    list,
    locales,
  ]);
  const previewColumns: ListColumn<ArcRow<V>>[] = [
    ...LOCALE_FIELDS.map(field => ({
      id: field,
      header: list.fieldLabels[field],
      // An empty cell is a locale not yet chosen, not a stored Any arc.
      cell: (row: ArcRow<V>): string => (row[field] === null ? '-' : display(row[field])),
    })),
    ...list.columns.map(({ key, header }) => ({
      id: key,
      header,
      cell: (row: ArcRow<V>): string => row[key],
    })),
  ];

  return (
    <SpreadsheetUpload
      {...uploadProps}
      columns={list.fields}
      fieldLabels={list.fieldLabels}
      toRow={list.toUploadRow}
      validate={validate}
      previewColumns={previewColumns}
      filterText={describeArc}
      compareRows={compareRows}
      filteringPlaceholder={ARC_FILTERING_PLACEHOLDER}
      ready={ready && locales !== null}
      onCreate={(rows): Promise<unknown> => onCreate(rows.map(list.toItem))}
      describeSaveError={describeSaveError || list.describeSaveError}
    />
  );
}
